import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Dropdown, Spinner } from "react-bootstrap";

import { PlanWeekType } from "../../DTO";
import { selectUser } from "../../redux-config/entities/user/selector";
import { useAppDispatch, useAppSelector } from "../../redux-config/hooks";
import { AppUserApi } from "../../api";
import { convertToPDTByLibrary } from "../../utils";
import { selectWeek, setWeek } from "../../redux-config/entities/week";
import "./style.scss";

type WeekListProps = {
    pageId: number;
    onWeekSelect?: (week: PlanWeekType) => void;
    variant?: "dropdown" | "grid";
};

const toISODate = (value?: string) => (value ? value.slice(0, 10) : "");
const formatWeekDate = (value?: string) => {
    if (!value) return "";

    return (convertToPDTByLibrary(value) || toISODate(value)).slice(0, 10);
};

export const WeekList: FC<WeekListProps> = ({
    pageId,
    onWeekSelect,
    variant = "dropdown",
}) => {
    const [loading, setLoading] = useState(false);
    const [weekList, setWeekList] = useState<PlanWeekType[]>([]);

    const user = useAppSelector(selectUser);
    const dispatch = useAppDispatch();
    const week = useAppSelector(selectWeek);

    const fetchPlanWeekList = useCallback(() => {
        if (!pageId) return;

        setLoading(true);

        AppUserApi.ListPlanUserWeek(user, Number(pageId), false)
            .then((data) => {
                const plan = data?.plan ?? [];
                setWeekList(plan);

                if (plan.length > 0 && (!week || week.index <= 0)) {
                    const current = plan.find((w) => w.isCurrentWeek) ?? plan[0];
                    dispatch(setWeek(current));
                    onWeekSelect?.(current);
                }
            })
            .catch((err) => {
                Swal.fire({
                    icon: "error",
                    text: err,
                });
            })
            .finally(() => setLoading(false));
    }, [user, pageId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchPlanWeekList();
    }, [fetchPlanWeekList]);

    const selectedLabel = useMemo(() => {
        if (loading) return "Loading...";
        if (week && week.index > 0) {
            const start = formatWeekDate(week.startDate);
            const end = formatWeekDate(week.endDate);
            return `Week ${week.index} • ${start} → ${end}`;
        }
        return "Choose Week";
    }, [loading, week]);

    const hasWeeks = weekList.length > 0;

    if (variant === "grid") {
        return (
            <div className="upr-week-grid-wrap">
                <div className="upr-week-grid-toolbar">
                    {loading && (
                        <div className="d-flex align-items-center gap-2 text-muted small">
                            <Spinner animation="border" size="sm" />
                            <span>Loading weeks...</span>
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none p-0 upr-week-grid-refresh"
                        onClick={fetchPlanWeekList}
                        disabled={loading}
                    >
                        Refresh
                    </button>
                </div>

                {!hasWeeks && !loading && (
                    <div className="upr-week-grid-empty">
                        No weeks found for this user.
                    </div>
                )}

                {hasWeeks && (
                    <div className="upr-week-grid">
                        {weekList.map((weekItem) => {
                            const isSelected = week?.index === weekItem.index;
                            const start = formatWeekDate(weekItem.startDate);
                            const end = formatWeekDate(weekItem.endDate);

                            return (
                                <button
                                    type="button"
                                    key={`${weekItem.index}-${weekItem.startDate}-${weekItem.endDate}`}
                                    className={[
                                        "upr-week-grid-item",
                                        isSelected ? "upr-week-grid-item-active" : "",
                                    ].join(" ")}
                                    onClick={() => {
                                        dispatch(setWeek(weekItem));
                                        onWeekSelect?.(weekItem);
                                    }}
                                >
                                    <div className="upr-week-grid-item-top">
                                        <span className="upr-week-grid-item-title">W{weekItem.index}</span>
                                        <span className="upr-week-grid-item-range">
                                            {start} - {end}
                                        </span>
                                        {weekItem.isCurrentWeek && (
                                            <span className="badge text-bg-primary upr-week-current-badge">Current</span>
                                        )}
                                        {isSelected && (
                                            <span className="upr-week-grid-pill">Selected</span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Dropdown className="upr-weeklist">
            <Dropdown.Toggle
                variant="outline-success"
                className="upr-pill upr-week-toggle"
            >
                <span className="d-inline-flex align-items-center gap-2">
                    {loading && <Spinner animation="border" size="sm" />}
                    <span className="upr-week-toggle-text">
                        {selectedLabel}
                    </span>
                </span>
            </Dropdown.Toggle>

            <Dropdown.Menu className="upr-week-menu">
                <div className="px-3 pt-2 pb-1">
                    <div className="fw-semibold">Weeks</div>
                    <div className="text-muted small">
                        Select a week to view its report
                    </div>
                </div>

                <Dropdown.Divider />

                {!hasWeeks && !loading && (
                    <div className="px-3 py-3 text-muted small">
                        No weeks found for this user.
                    </div>
                )}

                {hasWeeks && (
                    <div className="upr-week-scroll">
                        {weekList.map((weekItem) => {
                            const isSelected = week?.index === weekItem.index;
                            const start = formatWeekDate(weekItem.startDate);
                            const end = formatWeekDate(weekItem.endDate);

                            return (
                                <Dropdown.Item
                                    key={`${weekItem.index}-${weekItem.startDate}-${weekItem.endDate}`}
                                    active={isSelected}
                                    className="upr-week-item"
                                    onClick={() => {
                                        dispatch(setWeek(weekItem));
                                        onWeekSelect?.(weekItem);
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                        <div className="fw-semibold">
                                            Week {weekItem.index}
                                        </div>
                                        <div className="d-flex gap-1">
                                            {weekItem.isCurrentWeek && (
                                                <span className="badge text-bg-primary">Current</span>
                                            )}
                                            {isSelected && (
                                                <span className="badge text-bg-success">Selected</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-muted small mt-1">
                                        {start} → {end}
                                    </div>
                                </Dropdown.Item>
                            );
                        })}
                    </div>
                )}

                <Dropdown.Divider />

                <button
                    type="button"
                    className="btn btn-link w-100 text-decoration-none py-2 upr-week-refresh"
                    onClick={fetchPlanWeekList}
                    disabled={loading}
                >
                    Refresh list
                </button>
            </Dropdown.Menu>
        </Dropdown>
    );
};
